// Tablero (Kanban) de tickets — Fase 6 de la integración CRM en Operations.
// Reimplementa el board del CRM (GET /tickets/board) contra el MISMO BFF, así
// que editar aquí edita en los dos sitios sin sync extra. Drag&drop entre stages
// con @dnd-kit/core: al soltar se hace optimistic update (la tarjeta se mueve
// localmente al instante) + POST /tickets/:id/move; si el POST falla, se
// revierte moviendo ESA tarjeta de vuelta y se muestra el error. Reasignación
// rápida por tarjeta (POST /tickets/:id/assign) con el mismo patrón optimista.
//
// No hay espejo local: el board vive en `board.data` (useCrmResource) y cada
// mutación optimista es un update FUNCIONAL por ticket sobre `board.setData`.
// Así un refetch en vuelo es el nuevo `prev` y no pisa el move; y tras cada
// move/assign OK hacemos `board.reload()` para reconciliar el 204 silencioso
// del CRM (contadores/hasMore incluidos) sin perder el optimismo.
//
// v2 (fuera de alcance esta fase, anotado en el roadmap §Fase 6): sincronía en
// tiempo real vía Mercure (cookie mercureAuthorization cross-subdominio). Aquí la
// lectura se sincroniza por refresco manual/recarga; la ESCRITURA sí es inmediata
// en ambos sentidos (Operations ↔ CRM comparten BD).

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { useCrmResource } from "../../hooks/useCrmResource";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  assignTicket,
  getBoard,
  listTicketPipelines,
  moveTicket,
  PRIORITY_LABELS,
} from "../../api/crmTickets";
import {
  TicketBoardColumn,
  TicketPipeline,
  TicketPriority,
  TicketsBoardResponse,
  TicketSummary,
} from "../../api/crmTypes";
import { CrmConnectGate } from "../../context/CrmSessionContext";
import { CrmHttpError, describeCrmError } from "../../api/crm";
import { Select } from "../../components/FormControls";
import CreateTicketModal from "../../components/crm/CreateTicketModal";
import Pill from "../LeadDetail/components/Pill";
import {
  FilterCell,
  errorBox,
  inputStyle,
  retryInline,
  viewToggleLink,
} from "../../components/crm/TableChrome";
import { useAlert, usePrompt } from "../../components/Dialog";
import { fmtDate } from "../../types/promos";

// Límite por columna: reutiliza el default del contrato (50). El board indica
// hasMore por columna cuando hay más de los que caben.
const LIMIT_PER_COLUMN = 50;

const PRIORITY_SCHEME: Record<
  TicketPriority,
  "muted" | "blue" | "amber" | "purple"
> = {
  low: "muted",
  medium: "blue",
  high: "amber",
  urgent: "purple",
};

// ── Helpers puros de mutación optimista sobre el board del servidor ──────────
// Operan sobre `board.data` (o null) y devuelven un nuevo objeto, para usarse
// como updater funcional de `board.setData`. Al mover POR TICKET (no re-plantar
// una foto), moves solapados y refetches en vuelo componen sin pisarse.

function moveTicketInBoard(
  prev: TicketsBoardResponse | null,
  ticketId: string,
  toStageId: string,
): TicketsBoardResponse | null {
  if (!prev) return prev;
  const target = prev.columns.find((c) => c.stageId === toStageId);
  // Encuentra la tarjeta esté donde esté ahora (su columna actual puede haber
  // cambiado por un move previo o un refetch).
  let ticket: TicketSummary | undefined;
  for (const c of prev.columns) {
    const found = c.tickets.find((t) => t.id === ticketId);
    if (found) {
      ticket = found;
      break;
    }
  }
  // Si ya está en destino o no existe/target desconocido, no toques nada.
  if (!target || !ticket || ticket.stageId === toStageId) return prev;

  const moved: TicketSummary = {
    ...ticket,
    stageId: target.stageId,
    stageLabel: target.stageLabel,
    stageState: target.ticketState,
  };
  return {
    ...prev,
    columns: prev.columns.map((c) => {
      if (c.stageId === toStageId) {
        return { ...c, tickets: [moved, ...c.tickets.filter((t) => t.id !== ticketId)] };
      }
      return { ...c, tickets: c.tickets.filter((t) => t.id !== ticketId) };
    }),
  };
}

function setAssigneeInBoard(
  prev: TicketsBoardResponse | null,
  ticketId: string,
  assignee: string | null,
): TicketsBoardResponse | null {
  if (!prev) return prev;
  return {
    ...prev,
    columns: prev.columns.map((c) => ({
      ...c,
      tickets: c.tickets.map((t) =>
        t.id === ticketId ? { ...t, assignedTo: assignee } : t,
      ),
    })),
  };
}

// Aviso de error de mutación. Nunca silencioso (salvo 401, que gestiona el gate):
// 403 → permisos; otro CrmHttpError → describeCrmError; no-CrmHttpError → conexión.
function notifyMutationError(
  err: unknown,
  verb: "mover" | "reasignar",
  alert: ReturnType<typeof useAlert>,
): void {
  const action = verb === "mover" ? "mover" : "reasignar";
  if (err instanceof CrmHttpError) {
    if (err.status === 401) return; // el gate maneja la reconexión
    const message =
      err.status === 403
        ? `No tienes permisos para ${action} tickets.`
        : describeCrmError(err);
    void alert({
      title: verb === "mover" ? "No se pudo mover el ticket" : "No se pudo reasignar",
      message,
      tone: "danger",
    });
    return;
  }
  void alert({
    title: verb === "mover" ? "No se pudo mover el ticket" : "No se pudo reasignar",
    message: "No se pudo conectar con el CRM. Revisa tu conexión e inténtalo de nuevo.",
    tone: "danger",
  });
}

export default function TicketsBoard() {
  return (
    <div style={{ padding: "1.5rem" }}>
      <CrmConnectGate>
        <BoardInner />
      </CrmConnectGate>
    </div>
  );
}

function BoardInner() {
  const navigate = useNavigate();
  const alert = useAlert();
  const prompt = usePrompt();

  const [pipelineId, setPipelineId] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  // Debounce del filtro por asignado (email) para no refetchear por cada tecla.
  const assignedToQ = useDebouncedValue(assignedTo.trim(), 300);

  const pipelines = useCrmResource(
    () => listTicketPipelines().then((ps) => ps.filter((p) => !p.archived)),
    [],
    "No se pudieron cargar los pipelines",
  );

  // Selecciona el primer pipeline en cuanto cargan (el board siempre está
  // scopeado a uno; sin pipeline no hay board que pedir).
  useEffect(() => {
    if (!pipelineId && pipelines.data && pipelines.data.length > 0) {
      setPipelineId(pipelines.data[0].id);
    }
  }, [pipelines.data, pipelineId]);

  // Board del pipeline seleccionado. `enabled` implícito: si no hay pipeline,
  // el fetcher devuelve null y no pega al CRM.
  const board = useCrmResource<TicketsBoardResponse | null>(
    () =>
      pipelineId
        ? getBoard({
            pipelineId,
            limitPerColumn: LIMIT_PER_COLUMN,
            ...(assignedToQ ? { assignedTo: assignedToQ } : {}),
          })
        : Promise.resolve(null),
    [pipelineId, assignedToQ],
    "No se pudo cargar el tablero",
  );

  // Único origen de verdad: las columnas son las del servidor en `board.data`.
  // Las mutaciones optimistas se aplican con updates funcionales sobre
  // `board.setData`, así que no hay espejo que pueda desincronizarse.
  const columns = board.data?.columns ?? null;

  const sensors = useSensors(
    // activationConstraint por distancia: no se come el click sobre la tarjeta
    // (abrir detalle) ni sobre el Link del lead.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const pipelineOptions = useMemo(
    () =>
      (pipelines.data ?? []).map((p: TicketPipeline) => ({
        value: p.id,
        label: p.label,
      })),
    [pipelines.data],
  );

  // ── Mover: optimistic update funcional + POST; en error revierte y avisa. ──
  const doMove = useCallback(
    (ticketId: string, fromStageId: string, toStageId: string) => {
      if (fromStageId === toStageId) return;

      // Optimista: mueve ESA tarjeta de origen a destino sobre el `prev` vivo.
      // Al ser funcional, un refetch en vuelo se convierte en el nuevo `prev` y
      // no se pierde el move.
      board.setData((prev) => moveTicketInBoard(prev, ticketId, toStageId));

      moveTicket(ticketId, { stageId: toStageId })
        .then(() => {
          // El CRM responde 204 (silencioso, incluso si el ticket no existe):
          // recarga para reconciliar datos reales, contadores y hasMore. Con el
          // diseño funcional, el reload no pisa nada.
          board.reload();
        })
        .catch((err) => {
          // Revierte moviendo ESA tarjeta de vuelta (no re-planta una foto), así
          // otros moves solapados no se ven afectados.
          board.setData((prev) => moveTicketInBoard(prev, ticketId, fromStageId));
          notifyMutationError(err, "mover", alert);
        });
    },
    [board, alert],
  );

  // ── Reasignar rápido: prompt de email (o vacío = sin asignar) + optimista. ──
  const doAssign = useCallback(
    async (ticket: TicketSummary) => {
      const value = await prompt({
        title: "Reasignar ticket",
        message: `Email del agente para "${ticket.subject}" (vacío = sin asignar).`,
        defaultValue: ticket.assignedTo ?? "",
        placeholder: "agente@life5.com",
        type: "email",
        okLabel: "Asignar",
        validate: (v) => {
          const t = v.trim();
          if (t && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(t)) return "Email no válido";
          return null;
        },
      });
      if (value === null) return; // cancelado
      const next = value.trim() || null;
      const prevAssignee = ticket.assignedTo ?? null;
      if (next === prevAssignee) return;

      board.setData((prev) => setAssigneeInBoard(prev, ticket.id, next));

      assignTicket(ticket.id, { assignee: next })
        .then(() => board.reload())
        .catch((err) => {
          board.setData((prev) => setAssigneeInBoard(prev, ticket.id, prevAssignee));
          notifyMutationError(err, "reasignar", alert);
        });
    },
    [board, prompt, alert],
  );

  // Estable para no romper el memo de TicketCard (depende solo de navigate).
  const onOpen = useCallback(
    (id: string) => navigate(`/operations/tickets/${id}`),
    [navigate],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const ticketId = String(event.active.id);
      const toStage = event.over ? String(event.over.id) : null;
      const fromStage = (
        event.active.data.current as { stageId?: string } | undefined
      )?.stageId;
      if (!toStage || !fromStage) return;
      doMove(ticketId, fromStage, toStage);
    },
    [doMove],
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "1.1rem", color: "#2A3F54" }}>
          Tablero de tickets
        </h1>
        <Link to="/operations/tickets" style={viewToggleLink}>
          Lista
        </Link>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={board.reload}
          disabled={board.loading || !pipelineId}
          style={ghostBtn(board.loading || !pipelineId)}
        >
          Refrescar
        </button>
        <button type="button" onClick={() => setCreateOpen(true)} style={addBtn}>
          + Crear ticket
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: "0.6rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <FilterCell label="Pipeline">
          <Select
            value={pipelineId}
            onChange={setPipelineId}
            options={pipelineOptions}
            placeholder={pipelines.loading ? "Cargando…" : "Elige pipeline"}
          />
        </FilterCell>
        <FilterCell label="Asignado (email)">
          <input
            type="text"
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            placeholder="agente@life5.com"
            style={inputStyle}
          />
        </FilterCell>
      </div>

      {pipelines.error && (
        <div style={errorBox}>
          {pipelines.error}{" "}
          <button type="button" onClick={pipelines.reload} style={retryInline}>
            Reintentar
          </button>
        </div>
      )}
      {board.error && (
        <div style={errorBox}>
          {board.error}{" "}
          <button type="button" onClick={board.reload} style={retryInline}>
            Reintentar
          </button>
        </div>
      )}

      {!pipelines.error &&
        !pipelines.loading &&
        (pipelines.data ?? []).length === 0 && (
          <EmptyBox>No hay pipelines disponibles.</EmptyBox>
        )}

      {board.loading && !columns && (
        <div style={{ color: "#73879C", fontSize: ".85rem" }}>
          Cargando tablero…
        </div>
      )}

      {!board.loading && !board.error && columns && columns.length === 0 && (
        <EmptyBox>Este pipeline no tiene etapas.</EmptyBox>
      )}

      {columns && columns.length > 0 && (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div style={{ overflowX: "auto", paddingBottom: "0.5rem" }}>
            <div
              style={{
                display: "flex",
                gap: "0.75rem",
                minWidth: "min-content",
                alignItems: "flex-start",
              }}
            >
              {columns.map((col) => (
                <BoardColumn
                  key={col.stageId}
                  column={col}
                  onOpen={onOpen}
                  onAssign={doAssign}
                />
              ))}
            </div>
          </div>
        </DndContext>
      )}

      {createOpen && (
        <CreateTicketModal
          pipelines={pipelines.data ?? undefined}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            board.reload();
          }}
        />
      )}
    </div>
  );
}

function BoardColumn({
  column,
  onOpen,
  onAssign,
}: {
  column: TicketBoardColumn;
  onOpen: (id: string) => void;
  onAssign: (t: TicketSummary) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.stageId });
  const closed = column.ticketState === "closed";
  return (
    <div
      ref={setNodeRef}
      style={{
        width: 280,
        flexShrink: 0,
        background: isOver ? "#eef5ff" : "#f8f9fb",
        border: `1px solid ${isOver ? "#90c2ff" : "#e9ecef"}`,
        borderRadius: "0.55rem",
        padding: "0.6rem",
        maxHeight: "calc(100vh - 220px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          marginBottom: "0.6rem",
        }}
      >
        <span
          style={{
            fontSize: ".8rem",
            fontWeight: 700,
            color: closed ? "#73879C" : "#2A3F54",
          }}
        >
          {column.stageLabel}
        </span>
        <span
          style={{
            fontSize: ".72rem",
            color: "#73879C",
            background: "#e9ecef",
            borderRadius: "0.6rem",
            padding: "0.05rem 0.4rem",
          }}
        >
          {column.tickets.length}
          {column.hasMore ? "+" : ""}
        </span>
      </div>
      <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {column.tickets.length === 0 && (
          <div
            style={{
              fontSize: ".75rem",
              color: "#adb5bd",
              textAlign: "center",
              padding: "0.8rem 0",
            }}
          >
            Sin tickets
          </div>
        )}
        {column.tickets.map((t) => (
          <TicketCard key={t.id} ticket={t} onOpen={onOpen} onAssign={onAssign} />
        ))}
        {column.hasMore && (
          <div style={{ fontSize: ".72rem", color: "#adb5bd", textAlign: "center" }}>
            Hay más tickets — usa la lista para verlos todos.
          </div>
        )}
      </div>
    </div>
  );
}

type TicketCardProps = {
  ticket: TicketSummary;
  onOpen: (id: string) => void;
  onAssign: (t: TicketSummary) => void;
};

function TicketCardBase({ ticket, onOpen, onAssign }: TicketCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: ticket.id,
    data: { stageId: ticket.stageId },
  });
  const closed = ticket.stageState === "closed";
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onOpen(ticket.id)}
      style={{
        background: "white",
        border: "1px solid #e9ecef",
        borderRadius: "0.45rem",
        padding: "0.55rem 0.6rem",
        boxShadow: "0 1px 2px rgba(0,0,0,.04)",
        cursor: "grab",
        opacity: isDragging ? 0.4 : closed ? 0.8 : 1,
        display: "flex",
        flexDirection: "column",
        gap: "0.35rem",
      }}
    >
      <div style={{ fontSize: ".8rem", fontWeight: 600, color: "#2A3F54" }}>
        {ticket.subject}
      </div>
      <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", alignItems: "center" }}>
        {ticket.priority && (
          <Pill
            label={PRIORITY_LABELS[ticket.priority]}
            scheme={PRIORITY_SCHEME[ticket.priority]}
          />
        )}
        {ticket.incidentType && (
          // Pill discreta con la Opción 1; el título completo en el tooltip por
          // si el label es largo.
          <span
            title={ticket.incidentType}
            style={{
              fontSize: ".68rem",
              color: "#495057",
              background: "#f1f3f5",
              border: "1px solid #e9ecef",
              borderRadius: "0.6rem",
              padding: "0.05rem 0.4rem",
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {ticket.incidentType}
          </span>
        )}
        {ticket.leadId && (
          <Link
            to={`/leads/${ticket.leadId}`}
            onClick={(e) => e.stopPropagation()}
            // onPointerDown detiene la propagación para que el drag no se active
            // al hacer click en el link del lead.
            onPointerDown={(e) => e.stopPropagation()}
            style={{ color: "#0069d9", fontSize: ".72rem", fontWeight: 600 }}
          >
            Ver lead
          </Link>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.4rem",
          fontSize: ".72rem",
          color: "#73879C",
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAssign(ticket);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          title="Reasignar"
          style={assignBtn}
        >
          {ticket.assignedTo ?? "Sin asignar"}
        </button>
        <span>{fmtDate(ticket.createdAt)}</span>
      </div>
    </div>
  );
}

// Tarjeta memoizada: en un board grande cada tarjeta es un draggable y no debe
// re-renderizar salvo que cambie su ticket (por campos pintados) o los handlers.
const TicketCard = memo(TicketCardBase, (a, b) => {
  const x = a.ticket;
  const y = b.ticket;
  return (
    x.id === y.id &&
    x.subject === y.subject &&
    x.stageId === y.stageId &&
    x.stageState === y.stageState &&
    x.priority === y.priority &&
    x.incidentType === y.incidentType &&
    x.assignedTo === y.assignedTo &&
    x.leadId === y.leadId &&
    x.createdAt === y.createdAt &&
    a.onOpen === b.onOpen &&
    a.onAssign === b.onAssign
  );
});

function EmptyBox({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "white",
        borderRadius: "0.55rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        padding: "1.5rem",
        textAlign: "center",
        color: "#73879C",
        fontSize: ".85rem",
      }}
    >
      {children}
    </div>
  );
}

const addBtn: React.CSSProperties = {
  padding: "0.45rem 0.9rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtn = (disabled: boolean): React.CSSProperties => ({
  padding: "0.45rem 0.9rem",
  borderRadius: "0.4rem",
  background: "white",
  color: disabled ? "#adb5bd" : "#2A3F54",
  border: "1px solid #ced4da",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: disabled ? "default" : "pointer",
});

const assignBtn: React.CSSProperties = {
  background: "#f1f3f5",
  border: "1px solid #dee2e6",
  borderRadius: "0.35rem",
  padding: "0.1rem 0.4rem",
  fontSize: ".72rem",
  color: "#495057",
  cursor: "pointer",
  maxWidth: 150,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
